import { ICourse } from '@/types/course'
import { IListingsCourseHit } from '@/types/search'
import { Typography } from '@mui/material'
import classNames from 'classnames'
import useEmblaCarousel from 'embla-carousel-react'
import { ComponentProps, memo, useMemo } from 'react'
import { InstantSearch, useHits } from 'react-instantsearch'
import resolveConfig from 'tailwindcss/resolveConfig'
import { useTranslations } from 'use-intl'
import tailwindConfig from '../../../../../tailwind.config'
import { adaptCourseCollectionToCourseCard } from '../../search/lib/course-adapter'
import { getSearchClient } from '../../search/lib/search-client'
import { useLanguageSelection } from '../../shared/providers/LanguageSelectionProvider'
import { NewCourseCard } from './NewCourseCard'

type CourseRelatedCourses = ComponentProps<'div'> & {
  courseId: string
  courseCategories: ICourse['categories']
  searchScopedKey: string
}

function CourseRelatedCourses({
  courseId,
  courseCategories = [],
  searchScopedKey,
  ...props
}: CourseRelatedCourses) {
  const { currentLocale } = useLanguageSelection()
  const t = useTranslations('CoursePage')

  const subCategories = courseCategories.flatMap(
    ({ subCategories }) => subCategories ?? []
  )

  const searchClient = useMemo(
    () =>
      getSearchClient('', {
        collectionSpecificSearchParameters: {
          courses: {
            q: '*',
            query_by: `name_${currentLocale}`,
            limit: 6,
            sort_by: 'quality_score:desc,_rand():asc',
            filter_by: `published:=true&&id:!=\"${courseId}\"&&subcategories.slug:=[${subCategories.map(({ slug }) => `'${slug}'`).join(',')}]`,
            'x-typesense-api-key': searchScopedKey,
          },
        },
      }),
    [subCategories, searchScopedKey, currentLocale, courseId]
  )

  if (!searchScopedKey || subCategories.length === 0) {
    return null
  }

  return (
    <InstantSearch
      indexName="courses"
      searchClient={searchClient}
      numberLocale={currentLocale}
      stalledSearchDelay={500}
      routing={false}
    >
      <div className={classNames('bg-primary', props.className)}>
        <div className="container py-14">
          <div className="flex flex-col gap-8">
            <Typography variant="h2" className="text-white">
              {t('related-courses')}
            </Typography>
            <CourseRecommendationsCarousel />
          </div>
        </div>
      </div>
    </InstantSearch>
  )
}

function CourseRecommendationsCarousel(props: ComponentProps<'div'>) {
  const { currentLocale } = useLanguageSelection()
  const { items: hits } = useHits<IListingsCourseHit<typeof currentLocale>>()
  const { theme } = resolveConfig(tailwindConfig)

  const [emblaRef] = useEmblaCarousel({
    align: 'start',
    breakpoints: {
      [`(min-width: ${theme?.screens?.['md']})`]: {
        slidesToScroll: 3,
        align: 'start',
      },
    },
  })

  return (
    <div
      className={classNames(
        'relative lg:w-max lg:max-w-[100%] lg:p-0',
        props.className
      )}
      ref={emblaRef}
      data-embla-ref
    >
      <ol className="no-scrollbar flex cursor-grab auto-cols-[minmax(calc(33%),300px)] justify-start gap-8 lg:grid lg:grid-flow-col lg:px-0">
        {hits.map((hit) => (
          <li
            key={hit.id}
            className="flex h-full min-h-56 shrink-0 basis-[300px] snap-start flex-col items-start gap-3 overflow-hidden md:basis-[calc(30%-2rem)] lg:basis-[calc(25%-2rem)]"
          >
            <NewCourseCard
              className="h-full"
              course={adaptCourseCollectionToCourseCard<typeof currentLocale>(
                hit,
                currentLocale
              )}
            />
          </li>
        ))}
      </ol>
    </div>
  )
}

const MemoizedCourseRelatedCourses = memo(CourseRelatedCourses)

export { MemoizedCourseRelatedCourses as CourseRelatedCourses }
